const ProjectUpdater = {
  /**
   * Creates custom rule instances from references to Custom Rule IDs
   * in objects and other custom rules
   */
  createCRInstances: function () {
    hsProject.version = Math.max(hsProject.version, 34)
    hsProject.customRuleInstances = []
    hsProject.objects.concat(hsProject.customRules).forEach(item => {
      item.rules.forEach((id, index) => {
        // For every Custom Rule ID in an object or custom rule, create a
        // custom rule instance and replace that rule ID
        const customRule = ProjectCustomRule.get(id)
        if (!customRule) return
        const crInstance = new ProjectCustomRuleInstance(customRule.parameters, id)
        item.rules[index] = crInstance.id
      })
    })
  },
  /**
   * Adds necessary properties to the project if they don't exist. This is
   * so the project won't fail to render when it tries to read these traits
   */
  addProperties: function () {
    const properties = [
      'abilities',
      'customObjects',
      'eventParameters',
      'objects',
      'remote_asset_urls',
      'rules',
      'traits',
      'variables'
    ]
    properties.forEach(property => {
      if (!hsProject[property]) hsProject[property] = []
    })
  },
  /**
   * For all rules that don't have their own ID, create an ID and
   * reference it in the target object's rules array
   */
  assignObjectRules: function () {
    if (!hsProject.rules) return
    hsProject.rules.forEach(rule => {
      if (rule.id) return
      // Assign UUID if it does not exist, then add that to the object
      rule.id = tempUuid().toUpperCase()
      const obj = ProjectStageObject.get(rule.objectID)
      if (!obj) return
      if (!obj.rules) obj.rules = []
      obj.rules.push(rule.id)
    })
  },
  /**
   * Performs all necessary upgrades on the project for the web editor
   * to function properly
   */
  run: function () {
    if (hsProject.customRules && !hsProject.customRuleInstances) this.createCRInstances()
    this.addProperties()
    this.assignObjectRules()
  }
}

class ProjectVariable {
  /**
   * Creates a variable in the project JSON from a set of given properties
   * @param {string} name The name to give to the new variable
   * @param {number} [type=8003] The variable type, where 8003 is game, 8000
   * is object, 8007 is user, and 8008 is product
   * @param {string} [id] The ID of the new variable
   * @returns {Object} The newly created variable
   */
  constructor (name, type = 8003, id) {
    const variable = {
      objectIdString: id || tempUuid().toUpperCase(),
      name,
      type
    }
    hsProject.variables.push(variable)
    ProjectVariable.reloadKeyboard(type)
    return variable
  }

  /**
   * Creates a variable in the project JSON from a set of given properties
   * @param {string} name The name to give to the new variable
   * @param {number} [type=8003] The variable type, where 8003 is game, 8000
   * is object, 8007 is user, and 8008 is product
   * @param {string} [id] The ID of the new variable
   * @returns {Object} The newly created variable
   */
  static create (name, type = 8003, id) {
    return new ProjectVariable(name, type, id)
  }

  /**
   * Returns the variable with the provided ID
   * @param {string} id The ID of the variable to retrieve
   * @param {boolean} [createIfEmpty] Whether to create a variable
   * if the specified one doesn't exist
   * @param {string} [creationName] The name to give the variable
   * if it is necessary to create one
   * @param {number} [type] The type to give the variable
   * if it is necessary to create one
   * @returns The matching variable, if any
   */
  static get (id, createIfEmpty, creationName, type) {
    const variable = hsProject.variables.find(v => v.objectIdString === id)
    if (variable) return variable
    if (!createIfEmpty) return
    return new ProjectVariable(creationName, type, id)
  }

  /**
   * Creates or overwrites a variable with the specified ID
   * @param {string} id The ID of the variable to write to
   * @param {Object} data The JSON data of the new variable
   * @returns The project variable's JSON
   */
  static write (id, data) {
    const existingVar = ProjectVariable.get(id)
    if (!existingVar) hsProject.variables.push(data)
    const existingIndex = hsProject.variables.indexOf(existingVar)
    hsProject.variables[existingIndex] = data
    ProjectVariable.reloadKeyboard(data.type)
    return data
  }

  /**
   * Deletes an item from the project variables array
   * @param {string} id The ID of the variable to delete
   * @returns {Boolean} Whether the variable was successfully deleted
   */
  static delete (id) {
    const existingVar = ProjectVariable.get(id)
    const type = existingVar.type
    const existingIndex = hsProject.variables.indexOf(existingVar)
    if (existingIndex === -1) return false
    hsProject.variables.splice(existingIndex, 1)
    ProjectVariable.reloadKeyboard(type)
    return true
  }

  /**
   * Reloads the keyboard section associated with a certain variable type
   * @param {Number} type The type of variable keyboard to reload
   */
  static reloadKeyboard (type) {
    const keyboardContainer = document.querySelector(
      `.keyboard-blocks-container[data-variable-type="${type}"]`
    )
    if (keyboardContainer) {
      loadBlockList(nodeTree(keyboardContainer, 2), keyboardContainer)
    }
  }
}

class ProjectTrait {
  /**
   * Creates a trait in the project JSON from a set of given properties
   * @param {number} type The trait type, where 2000 is Rotation, 2001 is X Position, etc.
   * @param {string} [id] The ID of the new trait
   * @returns {Object} The newly created trait
   */
  constructor (type, id) {
    const label = blockLabels[type]
    const trait = {
      HSTraitIDKey: id || tempUuid().toUpperCase(),
      HSTraitObjectParameterTypeKey: 8000, // Only object traits are stored
      HSTraitTypeKey: type,
      description: label ? label[0] : 'Unknown'
    }
    hsProject.traits.push(trait)
    return trait
  }

  /**
   * Creates a trait in the project JSON from a set of given properties
   * @param {number} type The trait type, where 2000 is Rotation, 2001 is X Position, etc.
   * @param {string} [id] The ID of the new trait
   * @returns {Object} The newly created trait
   */
  static create (type, id) {
    return new ProjectTrait(type, id)
  }

  /**
   * Returns the trait with the provided ID
   * @param {string} id The ID of the trait to retrieve
   * @param {boolean} [createIfEmpty] Whether to create a trait
   * if the specified one doesn't exist
   * @param {string} [creationType] The type to give the trait
   * if it is necessary to create one
   * @returns The matching trait, if any
   */
  static get (id, createIfEmpty, creationType) {
    const trait = hsProject.traits.find(t => t.HSTraitIDKey === id)
    if (trait) return trait
    if (!createIfEmpty) return
    return new ProjectTrait(creationType, id)
  }

  /**
   * Creates or overwrites a trait with the specified UUID
   * @param {string} id The ID of the trait to write to
   * @param {Object} data The JSON data of the new trait
   * @returns The project trait's JSON
   */
  static write (id, data) {
    const existingTrait = ProjectTrait.get(id)
    if (!existingTrait) hsProject.traits.push(data)
    const existingIndex = hsProject.traits.indexOf(existingTrait)
    hsProject.traits[existingIndex] = data
    return data
  }
}

class ProjectEventParameter {
  /**
   * Creates an event parameter in the project JSON from a set of given properties
   * @param {number} type The event parameter type, where 8000 is object, 8003 is game,
   * and 8007 is user variable.
   * @param {string} [objectID] The ID of the object being referenced
   * @param {string} [id] The ID of the new event parameter
   * @returns {Object} The newly created event parameter
   */
  constructor (type, objectID, id) {
    const label = blockLabels[type]
    const eventParameter = {
      id: id || tempUuid().toUpperCase(),
      blockType: type,
      description: label ? label[0] : 'Object'
    }
    if (objectID) eventParameter.objectID = objectID
    hsProject.eventParameters.push(eventParameter)
    return eventParameter
  }

  /**
   * Creates an event parameter in the project JSON from a set of given properties
   * @param {number} type The event parameter type, where 8000 is object, 8003 is game,
   * and 8007 is user variable.
   * @param {string} [objectID] The ID of the object being referenced
   * @param {string} [id] The ID of the new event parameter
   * @returns {Object} The newly created event parameter
   */
  static create (type, objectID, id) {
    return new ProjectEventParameter(type, objectID, id)
  }

  /**
   * Returns the event parameter with the provided ID
   * @param {string} id The ID of the event parameter to retrieve
   * @param {boolean} [createIfEmpty] Whether to create a event parameter
   * if the specified one doesn't exist
   * @param {string} [creationType] The type to give the event parameter
   * if it is necessary to create one
   * @param {string} [creationType] The object ID for the event parameter
   * if it is necessary to create one
   * @returns The matching event parameter, if any
   */
  static get (id, createIfEmpty, creationType, creationObjectID) {
    const eventParameter = hsProject.eventParameters.find(p => p.id === id)
    if (eventParameter) return eventParameter
    if (!createIfEmpty) return
    return new ProjectEventParameter(creationType, creationObjectID, id)
  }

  /**
   * Creates or overwrites a event parameter with the specified UUID
   * @param {string} id The ID of the event parameter to write to
   * @param {Object} data The JSON data of the new event parameter
   * @returns The project event parameter's JSON
   */
  static write (id, data) {
    const existingEventParam = ProjectEventParameter.get(id)
    if (!existingEventParam) hsProject.eventParameters.push(data)
    const existingIndex = hsProject.eventParameters.indexOf(existingEventParam)
    hsProject.eventParameters[existingIndex] = data
    return data
  }
}

class ProjectAbility {
  static identifier = 'abilityID'
  static projectTraitName = 'abilities'

  /**
   * Creates an ability in the project JSON from a set of given properties
   * @param {Array} blocks An array of blocks to insert into the ability
   * @param {string} [id] The UUID to assign to the ability
   * @param {*} [name] The name of the ability
   * @returns {Object} The newly created ability
   */
  constructor (blocks = [], id, name) {
    const ability = {
      abilityID: id || tempUuid().toUpperCase(),
      name,
      blocks,
      createdAt: ProjectAbility.generateTime()
    }
    hsProject.abilities.push(ability)
    // NOTE: Refresh abilities if a new ability is created
    return ability
  }

  /**
   * Creates an ability in the project JSON from a set of given properties
   * @param {Array} blocks An array of blocks to insert into the ability
   * @param {string} [id] The UUID to assign to the ability
   * @param {*} [name] The name of the ability
   */
  static create (blocks = [], id, name) {
    return new ProjectAbility(blocks, id, name)
  }

  /**
   * Returns a timestamp more recent than all existing abilities
   * @returns {number} A createdAt timestamp that makes the ability the most recently created
   */
  static generateTime () {
    const lastCreate = hsProject.abilities.map(a => a.createdAt)
      .sort((b, a) => a - b)[0]
    return lastCreate + 12.345678
  }

  /**
   * Returns the ability with the provided UUID
   * @param {string} id The UUID of the Ability to retrieve
   * @param {boolean} createIfEmpty Whether to create an ability
   * if the specified one doesn't exist
   * @returns The matching ability, if any
   */
  static get (id, createIfEmpty) {
    const ability = hsProject.abilities.find(a => a.abilityID && a.abilityID === id)
    if (ability) return ability
    if (!createIfEmpty) return
    return new ProjectAbility([], id)
  }

  static matchingEditorBlocks (id) {
    const matches = blocksContainer.querySelectorAll(`.abl[data-id*="${id}"]`)
    // Convert to array
    return [...matches]
  }

  static matchingContainers (id) {
    const matches = blocksContainer.querySelectorAll(`.collapsible[data-ability*="${id}"]`)
    // Convert to array
    return [...matches]
  }

  /**
   * Creates or overwrites an ability with the specified UUID
   * @param {string} id The UUID of the ability to write to
   * @param {Object} data The JSON data of the new ability data
   * @returns The JSON ability
   */
  static write (id, data) {
    const existingAbility = ProjectAbility.get(id)
    if (!existingAbility) hsProject.abilities.push(data)
    const existingIndex = hsProject.abilities.indexOf(existingAbility)
    hsProject.abilities[existingIndex] = data
    return data
  }

  /**
   * Deletes the ability that matches the given ID
   * @param {string} id The ID of the ability to delete
   */
  static delete (id) {
    const existingAbility = ProjectAbility.get(id)
    if (!existingAbility) return
    const index = hsProject.abilities.indexOf(existingAbility)
    // Remove from abilities array
    hsProject.abilities.splice(index, 1)
  }
}

class ProjectRule {
  static identifier = 'id'
  static projectTraitName = 'rules'

  /**
   * Creates a rule in the project JSON from a set of given properties
   * @param {Object} [operator] The rule's datum operator
   * @param {string} [id] The UUID to assign to the rule
   * @param {string} [objectID] The Object ID to assign to the rule
   * @param {string} [abilityID] The UUID to assign to the rule's ability
   * @returns {Object} The newly created rule
   */
  constructor (operator, id, objectID = '', abilityID) {
    const ability = ProjectAbility.get(abilityID || tempUuid().toUpperCase(), true)
    if (operator && operator.params) {
      operator.params.forEach(param => {
        const innerTrait = param.datum
        if (!innerTrait || innerTrait.HSTraitIDKey === undefined) return
        // Generate new ID for traits
        innerTrait.HSTraitIDKey = tempUuid().toUpperCase()
      })
    }
    const rule = {
      id: id || tempUuid().toUpperCase(),
      objectID: objectID,
      abilityID: ability.abilityID,
      parameters: [{
        datum: operator,
        type: 52,
        value: ''
      }],
      ruleBlockType: 6000
    }
    hsProject.rules.push(rule)
    return rule
  }

  /**
   * Creates a rule in the project JSON from a set of given properties
   * @param {Object} [operator] The rule's datum operator
   * @param {string} [id] The UUID to assign to the rule
   * @param {string} [objectID] The Object ID to assign to the rule
   * @param {string} [abilityID] The UUID to assign to the rule's ability
   * @returns {Object} The newly created rule
   */
  static create (operator, id, objectID = '', abilityID) {
    return new ProjectCustomRule(operator, id, objectID, abilityID)
  }

  /**
   * Returns the rule with the provided UUID
   * @param {string} id The UUID of the rule to retrieve
   * @param {boolean} [createIfNone] Whether to create a rule if not found
   * @param {string} [creationObjectID] The Object ID to assign if a rule is created
   * if the specified one doesn't exist
   * @returns The matching rule, if any
   */
  static get (id, createIfNone, creationObjectID) {
    const rule = hsProject.rules.find(r => r.id === id)
    if (rule) return rule
    if (!createIfNone) return
    return new ProjectRule({}, id, creationObjectID)
  }

  /**
   * Creates or overwrites a rule with the specified UUID
   * @param {string} id The UUID of the rule to write to
   * @param {Object} data The JSON data of the new rule
   * @returns The JSON rule
   */
  static write (id, data) {
    const existingRule = ProjectRule.get(id)
    if (!existingRule) hsProject.rules.push(data)
    const existingIndex = hsProject.rules.indexOf(existingRule)
    hsProject.rules[existingIndex] = data
    return data
  }

  static matchingEditorBlocks (id) {
    const matches = blocksContainer.querySelectorAll(`.rule[data-id="${id}"]`)
    // Convert to array
    return [...matches]
  }
}

class ProjectCustomRule {
  static identifier = 'id'
  static projectTraitName = 'customRules'

  /**
   * Creates a custom rule in the project JSON from a set of given properties
   * @param {Array} [rules] An array of rule IDs to insert into the custom rule
   * @param {string} [id] The UUID to assign to the custom rule
   * @param {string} [name] The name of the custom rule
   * @returns {Object} The newly created custom rule
   */
  constructor (rules = [], id, name = '', abilityID) {
    const ability = ProjectAbility.get(abilityID || tempUuid().toUpperCase(), true)
    const customRule = {
      id: id || tempUuid().toUpperCase(),
      name,
      rules,
      abilityID: ability.abilityID
    }
    hsProject.customRules.push(customRule)
    // NOTE: Refresh custom rules in keyboard if a new ability is created
    return customRule
  }

  /**
   * Creates a custom rule in the project JSON from a set of given properties
   * @param {Array} [rules] An array of rule IDs to insert into the custom rule
   * @param {string} [id] The UUID to assign to the custom rule
   * @param {string} [name] The name of the custom rule
   */
  static create (rules = [], id, name = '', abilityID) {
    return new ProjectCustomRule(rules, id, name, abilityID)
  }

  /**
   * Returns the Custom Rule with the provided UUID
   * @param {string} id The UUID of the Custom Rule to retrieve
   * @param {boolean} createIfNone Whether to create an Custom Rule
   * if the specified one doesn't exist
   * @returns The matching custom rule, if any
   */
  static get (id, createIfNone) {
    const customRule = hsProject.customRules.find(cr => cr.id === id)
    if (customRule) return customRule
    if (!createIfNone) return
    return new ProjectCustomRule([], id)
  }

  static matchingEditorBlocks (id) {
    // TODO: change element `crule` class to `custom-rule`
    const customRuleBlocks = blocksContainer.querySelectorAll('div.crule[data-id]')
    const matches = [...customRuleBlocks].filter(block => {
      const data = getBlockJSON(block)
      return data.customRuleID === id
    })
    return matches
  }

  static matchingContainers (id) {
    const parentBlocks = this.matchingEditorBlocks(id)
    return parentBlocks.map(block => block.querySelector('.collapsible')).filter(x => !!x)
  }

  /**
   * Creates or overwrites a custom rule with the specified UUID
   * @param {string} id The UUID of the custom rule to write to
   * @param {Object} data The JSON data of the new custom rule data
   * @returns The custom rule JSON
   */
  static write (id, data) {
    const existingCustomRule = ProjectCustomRule.get(id)
    if (!existingCustomRule) hsProject.customRules.push(data)
    const existingIndex = hsProject.customRules.indexOf(existingCustomRule)
    hsProject.customRules[existingIndex] = data
    return data
  }

  /**
   * Deletes the custom rule that matches the given ID
   * @param {string} id The ID of the custom rule to delete
   */
  static delete (id) {
    const existingCustomRule = ProjectCustomRule.get(id)
    if (!existingCustomRule) return
    const index = hsProject.customRules.indexOf(existingCustomRule)
    // Remove from custom rules array
    hsProject.customRules.splice(index, 1)
  }

  /**
   * Returns a list of custom rules that the current custom rule is contained within
   * @param {Object} customRule The data of the custom rule to get the tree for
   * @returns {Array} An array of custom rules that contain the provided custom rule
   * (directly and indirectly)
   */
  static tree (customRule) {
    let parentCustomRules = [customRule]
    let i = 0
    const nestedArray = []
    while (parentCustomRules.length > 0 && i < hsProject.customRules.length) {
      const output = []
      parentCustomRules.forEach(cr => {
        nestedArray.push(cr)
        // Instances of the corresponding custom rule
        const instances = hsProject.customRuleInstances.filter(cri => {
          return cr.id === cri.customRuleID
        })
        // Find custom rules that contain instances of the first parent custom rule
        const parentCustomRules = instances.map(i => {
          return hsProject.customRules.find(cr => cr.rules.includes(i.id))
        }).filter(x => x)
        output.push(...parentCustomRules)
      })
      i++
      parentCustomRules = output
    }
    return nestedArray
  }
}

class ProjectCustomRuleInstance {
  static identifier = 'id'
  static projectTraitName = 'customRuleInstances'

  /**
   * Creates a custom rule in the project JSON from a set of given properties
   * @param {Array} [parameters] An array of custom rule instance's parameters
   * @param {string} [customRuleID] The ID of the custom rule
   * @param {string} [id] The UUID to assign to the custom rule instance
   * @returns {Object} The newly created custom rule
   */
  constructor (parameters = [], customRuleID, id) {
    const customRule = ProjectCustomRule.get(customRuleID, true)
    const customRuleInstance = {
      id: id || tempUuid().toUpperCase(),
      customRuleID: customRule.id,
      parameters
    }
    hsProject.customRuleInstances.push(customRuleInstance)
    return customRuleInstance
  }

  /**
   * Creates a custom rule in the project JSON from a set of given properties
   * @param {Array} [parameters] An array of custom rule instance's parameters
   * @param {string} [customRuleID] The ID of the custom rule
   * @param {string} [id] The UUID to assign to the custom rule instance
   * @returns {Object} The newly created custom rule
   */
  static create (parameters = [], customRuleID, id) {
    return new ProjectCustomRule(parameters, customRuleID, id)
  }

  /**
   * Returns the custom rule instance with the provided UUID
   * @param {string} id The UUID of the custom rule instance to retrieve
   * @param {boolean} [createIfNone] Whether to create an instance if not found
   * @param {boolean} [creationCustomRuleID] Whether to create an instance if not found
   * if the specified one doesn't exist
   * @returns The matching rule, if any
   */
  static get (id, createIfNone, creationCustomRuleID) {
    const crInstance = hsProject.customRuleInstances.find(r => r.id === id)
    if (crInstance) return crInstance
    if (!createIfNone) return
    const customRule = ProjectCustomRule.get(creationCustomRuleID, true)
    return new ProjectCustomRuleInstance({}, customRule.id, id)
  }

  static matchingEditorBlocks (id) {
    // TODO: change element `crule` class to `custom-rule`
    const matches = blocksContainer.querySelectorAll(`.crule[data-id*="${id}"]`)
    // Convert to array
    return [...matches]
  }

  /**
   * Creates or overwrites a custom rule instance with the specified UUID
   * @param {string} id The UUID of the custom rule instance to write to
   * @param {Object} data The JSON data of the new custom rule instance
   * @returns The JSON custom rule instance
   */
  static write (id, data) {
    const existingInstance = ProjectCustomRuleInstance.get(id)
    if (!existingInstance) hsProject.customRuleInstances.push(data)
    const existingIndex = hsProject.customRuleInstances.indexOf(existingInstance)
    hsProject.customRuleInstances[existingIndex] = data
    return data
  }
}

class ProjectStageObject {
  static identifier = 'objectID'
  static projectTraitName = 'objects'

  /**
   * Creates a stage object in the project JSON from a set of given properties
   * @param {string} [id] The UUID to assign to the object
   * @param {Object} [properties] A dictionary of object traits to assign
   * @returns {Object} The newly created object
   */
  constructor (id, properties = {}) {
    const ability = ProjectAbility.get(properties.abilityID || tempUuid().toUpperCase(), true)
    const stageObject = {
      objectID: id || tempUuid().toUpperCase(),
      abilityID: ability.abilityID,
      filename: 'text-object.png',
      height: 55,
      name: 'Text',
      resizeScale: 1,
      rules: [],
      text: '',
      type: 64,
      width: 74,
      xPosition: 512,
      yPosition: 384,
      ...properties
    }
    // Count up when there is a duplicate name, i.e. Bear, Bear 2, Bear 3
    let name = stageObject.name
    let index = 2
    while (ProjectStageObject.indexOf(name) !== -1) {
      name = `${stageObject.name} ${index}`
      index++
    }
    stageObject.name = name
    hsProject.objects.push(stageObject)
    return stageObject
  }

  /**
   * Creates a stage object in the project JSON from a set of given properties
   * @param {string} [id] The UUID to assign to the object
   * @param {Object} [properties] A dictionary of object traits to assign
   */
  static create (id, properties) {
    return new ProjectStageObject(id, properties)
  }

  /**
   * Returns the object with the provided UUID
   * @param {string} id The UUID of the object to retrieve
   * @param {boolean} createIfNone Whether to create an object
   * if the specified one doesn't exist
   * @returns The matching object, if any
   */
  static get (id, createIfNone) {
    const stageObject = hsProject.objects.find(obj => obj.objectID === id)
    if (stageObject) return stageObject
    if (!createIfNone) return
    return new ProjectStageObject([], id)
  }

  /**
   * Returns the index of an object with the matching name
   * @param {string} name The name of the object to get index of
   * @returns {number} The index of the matchingobject
   */
  static indexOf (name) {
    const stageObject = hsProject.objects.find(obj => obj.name === name)
    if (!stageObject) return -1
    return hsProject.objects.indexOf(stageObject)
  }

  static matchingEditorBlocks (id) {
    const matches = blocksContainer.querySelectorAll(`.obj[data-id*="${id}"]`)
    // Convert to array
    return [...matches]
  }

  /**
   * Creates or overwrites an object with the specified UUID
   * @param {string} id The UUID of the object to write to
   * @param {Object} data The JSON data of the new object data
   * @returns The object JSON
   */
  static write (id, data) {
    const existingObject = ProjectStageObject.get(id)
    if (!existingObject) hsProject.objects.push(data)
    const existingIndex = hsProject.objects.indexOf(existingObject)
    hsProject.objects[existingIndex] = data
    // Whenever we write data, reload the existing object references
    ProjectStageObject.rerenderReferences(id)
    return data
  }

  static rerenderReferences (id) {
    const object = ProjectStageObject.get(id) || { objectID: id }
    if (typeof BlockParameter === 'undefined') return
    const nameLabels = blocksContainer.querySelectorAll(`[data-object-id="${id}"]`)
    const parameters = [...nameLabels].map(label => label.parentNode)
    parameters.forEach(param => {
      param.innerHTML = ParameterRenderer.objectLabel(object)
    })
  }
}

class ProjectScene {
  static identifier = null
  static projectTraitName = 'scenes'

  /**
   * Creates a scene in the project JSON from a set of given properties
   * @param {number} [name] The name of the scene
   * @returns {Object} The newly created scene
   */
  constructor (name) {
    if (!hsProject.scenes) return {}
    const scene = {
      filename: '',
      objects: [],
      name: name || 'Scene ' + (hsProject.scenes.length + 1)
    }
    hsProject.scenes.push(scene)
    return scene
  }

  /**
   * Creates a scene in the project JSON from a set of given properties
   * @param {number} [name] The name of the scene
   */
  static create (name) {
    return new ProjectScene(name)
  }

  /**
   * Returns the index of a scene with the matching name
   * @param {string} name The name of the scene to get index of
   * @returns {number} The index of the scene
   */
  static indexOf (name) {
    const scene = hsProject.scenes.find(s => s.name === name)
    if (!scene) return -1
    return hsProject.scenes.indexOf(scene)
  }

  /**
   * Creates or overwrites a scene at the specified index
   * @param {number} index The UUID of the object to write to
   * @param {Object} data The JSON data of the new object data
   * @returns The object JSON
   */
  static write (index, data) {
    const existingScene = hsProject.scenes[index]
    if (!existingScene) hsProject.scenes.push(data)
    hsProject.scenes[index] = data
    return data
  }
}

class ProjectCustomImage {
  static objectURLs = {}
  static loaded = false

  static get (id) {
    return hsProject.customObjects.find(c => c.id === id)
  }

  static fireLoadEvent () {
    ProjectCustomImage.loaded = true
    const imageLoadEvent = new CustomEvent('project-images-loaded')
    window.dispatchEvent(imageLoadEvent)

    // Reload Custom Images in Keyboard
    if (typeof operatorsKeyboard === 'undefined') return
    const imgContainer = operatorsKeyboard.querySelector('[data-blocksource="custom-images"]')
    loadBlockList(imgContainer.parentNode, imgContainer)
    const imgObjContainer = blocksKeyboard.querySelector('[data-blocksource="custom-images"]')
    loadBlockList(imgObjContainer.parentNode, imgObjContainer)
  }

  static createObjectURLs () {
    const imageCount = hsProject.remote_asset_urls.length
    let loadedImageCount = 0
    function fireCompletionEvent () {
      if (loadedImageCount !== imageCount) return
      ProjectCustomImage.fireLoadEvent()
    }

    // Loop through remote asset URLs
    hsProject.remote_asset_urls.forEach(url => {
      // Get full custom image path
      const fullURL = `https://hopscotch-images.s3.amazonaws.com/${
          API.env_name === 'PRODUCTION' ? 'production' : 'staging'
        }/images/project-images/${url}`
      // Retrieve these images, then store local object URLs so loading
      // them in the project is much faster
      fetch(fullURL).then(img => img.blob()).then(data => {
        loadedImageCount++
        ProjectCustomImage.objectURLs[url] = URL.createObjectURL(data)
        fireCompletionEvent()
      })
    })

    if (hsProject.remote_asset_urls.length === 0) {
      ProjectCustomImage.loaded = true
    }
  }
}

const EditorAnalytics = {
  revisionSuffix: {
    Drag: ' (add/delete/rearrange in web)',
    BlockUpdate: ' (add/delete operator, update param value, rename object/scene in web)'
  },
  undoRedo: function (revision, isClick) {
    const lastRecord = revision.records.slice().reverse()[0]
    const codeName = revision.action + lastRecord.type
    // Convert codename `undoActionType` to `Undo Action Type (details)`
    const action = codeName.replace(/(\w)([A-Z])/g, '$1 $2')
      .replace(/./, s => s.toUpperCase()) + (this.revisionSuffix[lastRecord.type] || '')
    const prefix = isClick ? 'tapped to' : 'used system gesture to'
    const eventName = `${prefix} ${revision.action}`
    return sendEvent(eventName, { action, source: 'web editor' })
  },
  /**
   * Sends an Amplitude event for custom block updates
   * @param {Boolean} isNew Whether a new custom abiliy or rule is being created
   * @param {Object} data The new custom rule data
   * @param {Object} oldData The old custom rule data
   * @param {ProjectAbility, ProjectCustomRule} TargetClass The target editor class
   */
  customBlockUpdate: function (isNew, data, oldData, TargetClass) {
    // Send Analytics Events
    if (isNew && TargetClass === ProjectAbility) {
      return sendEvent('ability_created', { ability_new_name: data.name })
    }
    if (TargetClass === ProjectAbility) {
      return sendEvent('renamed an ability', {
        ability_new_name: data.name,
        ability_original_name: oldData.name
      })
    }
    if (isNew && TargetClass === ProjectCustomRule) {
      return sendEvent('created a custom rule', { name: data.name })
    }
    if (TargetClass === ProjectCustomRule) {
      return sendEvent('renamed a rule', {
        rule_new_name: data.name,
        rule_original_name: oldData.name
      })
    }
  },
  /**
   * Sends a drag or delete event based on a block drag event
   * @param {CustomEvent} event The Sortable drag event
   * @param {Boolean} isRemoval Whether the block was deleted
   */
  fromDragEvent: function (event, isRemoval) {
    const nameEl = event.item.querySelector('.block-name')
    const isMultiDrag = !!event.items && event.items.length > 1
    const blockname = nameEl ? nameEl.innerText : ''
    const eventName = isRemoval ? 'Deleted a block' : 'block_dropped'
    const details = { blockname }
    if (isRemoval) details.method = 'Dragged to trash'
    else details.multiDrag = isMultiDrag
    sendEvent(eventName, details)
    if (!isRemoval || !event.item.matches('.block-wrapper.obj')) return
    sendEvent('object_deleted', { name: blockname })
  },
  renameEvent: function (type, name, oldName) {
    const eventName = type === 'scene'
      ? 'renamed a scene'
      : 'renamed an object'
    const keys = ['new', 'original'].map(x => `${type}_${x}_name`)
    const properties = {
      [keys[0]]: name, // new_scene_name or new_object_name
      [keys[1]]: oldName // old_scene_name or old_object_name
    }
    sendEvent(eventName, properties)
  }
}

const blockLabels = {
  19: ['old', 'Wait til Timestamp', 'milliseconds'],
  20: ['old', '\u2063'], // Wait til Input Done
  22: ['old', '\u2063'],
  23: ['move', 'Move Forward', ''],
  24: ['move', 'Turn', 'degrees'],
  26: ['draw', 'Draw a Trail', 'color', 'width'],
  27: ['move', 'Change X', 'by'],
  28: ['move', 'Change Y', 'by'],
  29: ['old', 'Scale', 'by'],
  30: ['draw', 'Clear'],
  31: ['draw', 'Set Trail Width', 'to'],
  32: ['draw', 'Set Trail Color', 'to'],
  33: ['looks', 'Change Pose'],
  34: ['move', 'Set Speed', 'to'],
  35: ['ctrl', 'Wait', 'milliseconds'],
  36: ['old', 'Set Opacity', 'to'],
  37: ['old', 'Pen Down'],
  38: ['old', 'Pen Up'],
  39: ['move', 'Set Angle', ''],
  40: ['looks', 'Set Text', 'to', 'color'],
  41: ['move', 'Set Position', 'to x', 'y'],
  42: ['looks', 'Send To Back'],
  43: ['looks', 'Bring To Front'],
  44: ['var', 'Increase', '', 'by'],
  45: ['var', 'Set', '', 'to'],
  46: ['old', 'Move With Trail', 'distance'],
  47: ['looks', 'Set Invisibility', 'percent'],
  48: ['looks', 'Grow', 'by percent'],
  49: ['looks', 'Shrink', 'by percent'],
  50: ['move', 'Flip'],
  51: ['looks', 'Set Size', 'percent'],
  52: ['looks', 'Start Sound', '', 'wait'],
  53: ['ctrl', 'Create a Clone of This Object', 'times'],
  54: ['looks', 'Set Color', ''],
  55: ['ctrl', 'Destroy'],
  56: ['looks', 'Set Image', ''],
  57: ['looks', 'Set', 'width', 'height'],
  58: ['looks', 'Set Z Index', ''],
  59: ['move', 'Set Origin', 'to x', 'y'],
  60: ['move', 'Set Center', 'to x', 'y'],
  61: ['ctrl', 'Wait', 'seconds'],
  62: ['looks', 'Start Sound', '', 'wait'], // Start Sound Seconds
  63: ['var', 'Save Input', '', 'prompt'],
  64: ['looks', 'Set Text to Input', 'color'],
  65: ['looks', 'Play Note', '', 'rhythm'],
  66: ['looks', 'Set Tempo', ''],
  67: ['looks', 'Set Instrument', ''],
  68: ['ctrl', 'Open Project', ''],
  69: ['ctrl', 'Comment', ''],
  70: ['looks', 'Set Background', 'color'],
  71: ['draw', 'Set Trail Cap', 'to'],
  72: ['looks', 'Show Pop-up', 'message'],
  73: ['draw', 'Set Trail Opacity', 'percent'],
  120: ['ctrl', 'Repeat', 'times'],
  121: ['ctrl', 'Repeat Forever'],
  122: ['ctrl', 'Check Once If', ''],
  123: ['abl', 'Ability'],
  124: ['ctrl', 'Check If Else', ''],
  125: ['ctrl', 'Change Scene', 'to'],
  126: ['ctrl', 'Broadcast Message', 'named'],
  127: ['ctrl', 'Request Seeds', '', 'for'],
  233: ['Random'],
  234: ['XPos'],
  235: ['YPos'],
  236: ['Random110'],
  237: ['Random1100'],
  238: ['Random11000'],
  239: ['Variable'],
  1e3: ['conditional', '', '', '='],
  1001: ['conditional', '', '', '\u2260'],
  1002: ['conditional', '', '', '<'],
  1003: ['conditional', '', '', '>'],
  1004: ['conditional', '', '', 'and'],
  1005: ['conditional', '', '', 'or'],
  1006: ['conditional', '', '', '\u2265'],
  1007: ['conditional', '', '', '\u2264'],
  1008: ['conditional', '', '', 'matches'],
  1009: ['conditional', '', 'not'],
  1010: ['conditional', 'flipped'],
  1011: ['HS_END_OF_CONDITIONAL_OPERATORS'],
  2e3: ['Rotation'],
  2001: ['X Position'],
  2002: ['Y Position'],
  2003: ['Invisibility as a %'],
  2004: ['Size as a %'],
  2005: ['Speed'],
  2006: ['Clone Index'],
  2007: ['Total Clones'],
  2008: ['Width'],
  2009: ['Height'],
  2010: ['Z Index'],
  2011: ['Origin X'],
  2012: ['Origin Y'],
  2013: ['Center X'],
  2014: ['Center Y'],
  2015: ['Text'],
  2016: ['Tempo'],
  2017: ['Instrument'],
  2018: ['Name'],
  2019: ['HS_END_OF_OBJECT_TRAITS'],
  2500: ['\uD83D\uDCF1 Username'],
  2501: ['\uD83D\uDCF1 Time'],
  2502: ['\uD83D\uDCF1 Year'],
  2503: ['\uD83D\uDCF1 Month'],
  2504: ['\uD83D\uDCF1 Day'],
  2505: ['\uD83D\uDCF1 Hour'],
  2506: ['\uD83D\uDCF1 Minute'],
  2507: ['\uD83D\uDCF1 Second'],
  2508: ['HS_END_OF_USER_TRAITS'],
  3e3: ['\u25B6\uFE0F Width'],
  3001: ['\u25B6\uFE0F Height'],
  3002: ['\u25B6\uFE0F Tilt Up %'],
  3003: ['\u25B6\uFE0F Tilt Down %'],
  3004: ['\u25B6\uFE0F Tilt Left %'],
  3005: ['\u25B6\uFE0F Tilt Right %'],
  3006: ['\u25B6\uFE0F Last Touch X'],
  3007: ['\u25B6\uFE0F Last Touch Y'],
  3008: ['\u25B6\uFE0F Total Objects'],
  3009: ['HS_END_OF_STAGE_TRAITS'],
  4e3: ['math', '', '', '+'],
  4001: ['math', '', '', '\u2212'], // Subtract
  4002: ['math', '', '', '\u00D7'], // Multiply
  4003: ['math', '', '', '\u00F7'], // Divide
  4004: ['math', 'random', '', 'to'],
  4005: ['math', '', '', '^'], // Power
  4006: ['math', '\u221A', ''], // Square Root
  4007: ['math', 'sin', ''],
  4008: ['math', 'cos', ''],
  4009: ['math', 'round', ''],
  4010: ['math', 'absolute value', ''],
  4011: ['math', '', '', '%'], // Modulo
  4012: ['math', 'tan', ''],
  4013: ['math', 'arcsin', ''],
  4014: ['math', 'arccos', ''],
  4015: ['math', 'arctan', ''],
  4016: ['math', 'max', '', ''],
  4017: ['math', 'min', '', ''],
  4018: ['math', 'floor', '', ''],
  4019: ['math', 'ceil', '', ''],
  4020: ['HS_END_OF_MATH_OPERATORS'],
  5e3: ['ColorOperatorRandom'],
  5001: ['color', '', 'R', 'G', 'B'],
  5002: ['color', '', 'H', 'S', 'B'],
  5003: ['HS_END_OF_COLOR_OPERATORS'],
  6e3: ['rule', 'When'], // Rule
  6001: ['RulePreview'], // Rule Preview
  7e3: ['event', 'game starts'], // Event operator start
  7001: ['event', 'is tapped', ''],
  7002: ['event', '', '', 'is touching'],
  7003: ['event', 'is pressed', ''],
  7004: ['event', 'Tilted Right'], // EventOperatorTiltRight
  7005: ['event', 'Tilted Left'], // EventOperatorTiltLeft
  7006: ['event', 'Tilted Up'], // EventOperatorTiltUp
  7007: ['event', 'Tilted Down'], // EventOperatorTiltDown
  7008: ['event', '\uD83D\uDCF1 hears a loud noise'],
  7009: ['event', '\uD83D\uDCF1 is shaken'],
  7010: ['event', '', '', 'bumps'],
  7011: ['event', 'is swiped right', ''],
  7012: ['event', 'is swiped left', ''],
  7013: ['event', 'is swiped up', ''],
  7014: ['event', 'is swiped down', ''],
  7015: ['event', 'object is cloned'], // Enter the World
  7016: ['event', 'Editor Tilt Right'], // EventOperatorTiltRightEditor
  7017: ['event', 'Editor Tilt Left'], // EventOperatorTiltLeftEditor
  7018: ['event', 'Editor Tilt Up'], // EventOperatorTiltUpEditor
  7019: ['event', 'Editor Tilt Down'], // EventOperatorTiltDownEditor
  7020: ['event', 'is not pressed', ''],
  7021: ['event', 'game is playing'],
  7022: ['event', 'touch ends'],
  7023: ['event', '', 'I get a message'],
  7024: ['event', '', 'Message matches'],
  7025: ['event', '', '', 'is not touching'],
  7026: ['HS_END_OF_EVENT_OPERATORS'],
  8000: ['Object', true],
  8001: ['Any Object', true],
  8002: ['Edge', true],
  8003: ['\u25B6\uFE0F', false], // Game
  8004: ['Self', true],
  8005: ['Original Object', true],
  8006: ['\uD83D\uDCF1', false], // Local
  8007: ['\uD83D\uDCF1', false], // User
  8008: ['\uD83C\uDF81', false], // Product
  8009: ['Local'], // Scoped
  8010: ['HS_END_OF_EVENT_PARAMETER_BLOCKS'],
  9000: ['looks', 'character', 'in', 'at'],
  9001: ['looks', 'characters', 'in', 'between', 'and'],
  9002: ['looks', 'length', ''],
  9003: ['looks', 'join'],
  9004: ['HS_END_OF_TEXT_OPERATOR_BLOCKS']
}
const charLabels = {
  0: 'monkey',
  1: 'text-object',
  2: 'octopus',
  3: 'gorilla',
  4: 'cupcake',
  5: 'bear',
  6: 'dino',
  7: 'frog',
  8: 'greenman',
  9: 'mustache',
  10: 'spacepod',
  11: 'zombieBear',
  12: 'ghoulopus',
  13: 'bats',
  14: 'frankenrilla',
  15: 'jodyWitch',
  16: 'cauldron',
  17: 'pumpkin',
  18: 'broom',
  19: 'lantern',
  20: 'parrotFlying',
  21: 'mandrill',
  22: 'mosquito',
  23: 'missChief',
  24: 'venus',
  25: 'jeepers',
  26: 'banyan',
  27: 'stargirl',
  28: 'astro',
  29: 'chillanna',
  30: 'robo',
  31: 'raccoon',
  32: 'bird',
  33: 'HS_END_OF_CHARACTERS',
  34: 'square',
  35: 'circle',
  36: 'hexagon',
  37: 'triangle',
  38: 'rightTriangle',
  39: 'rectangle',
  40: 'heart',
  41: 'star',
  42: 'arch',
  43: 'parallelogram',
  44: 'squiggle',
  45: 'donut',
  46: 'tetrisZ',
  47: 'tetrisT',
  48: 'tetrisL',
  49: 'corner',
  50: 'flower',
  51: 'threeProngedBoomerang',
  52: 'squishedBox',
  53: 'bead',
  54: 'chevron',
  55: 'xShape',
  56: 'tetrisLine',
  57: 'HS_END_OF_SHAPES',
  58: 'toucan',
  59: 'anteater',
  60: 'crocodile',
  61: 'sloth',
  62: 'iguana',
  63: 'hut',
  64: 'penguin',
  65: 'winterQueen',
  66: 'shyYeti',
  67: 'deer',
  68: 'elf',
  69: 'snowGlobe',
  70: 'polarbear',
  71: 'sleigh',
  72: 'mistletoe',
  73: 'snowMan',
  74: 'snowflake',
  100: 'roundedSquareFullSize',
  101: 'squareFullSize',
  102: 'circleFullSize',
  103: 'hexagonFullSize',
  104: 'triangleFullSize',
  105: 'rightTriangleFullSize',
  106: 'rectangleFullSize',
  107: 'heartFullSize',
  108: 'starFullSize',
  109: 'archFullSize',
  110: 'parallelogramTallFullSize',
  111: 'squiggleFullSize',
  112: 'donutFullSize',
  113: 'tetrisZFullSize',
  114: 'tetrisTFullSize',
  115: 'tetrisLFullSize',
  116: 'cornerFullSize',
  117: 'flowerFullSize',
  118: 'fanbladeFullSize',
  119: 'squishedBoxFullSize',
  120: 'roundedRightTriangleFullSize',
  121: 'arrowRoundedFullSize',
  122: 'beadFullSize',
  123: 'parallelogramWideFullSize',
  124: 'chevronFullSize',
  125: 'xFullSize',
  126: 'tetrisLineFullSize',
  150: 'hexagonV3',
  151: 'triangleV3',
  152: 'rectangleV3',
  153: 'heartV3',
  154: 'starV3',
  155: 'archV3',
  156: 'squiggleV3',
  157: 'tetrisZV3',
  158: 'tetrisTV3',
  159: 'tetrisLV3',
  160: 'fanbladeV3',
  161: 'arrowRoundedV3',
  162: 'beadV3',
  163: 'parallelogramWideV3',
  164: 'chevronV3',
  165: 'HS_END_OF_FULL_SIZE_SHAPES',
  166: 'HS_NUMBER_OF_OBJECTS',
  2e3: 'image',
  3e3: 'HS_START_OF_CHARACTERS2',
  3001: 'crocodileJaws',
  3002: 'lanternFullSize',
  3003: 'HS_END_OF_CHARACTERS2',
  1e4: 'nil',
  3e4: 'edgeOfScreen'
}
const characterSizes = {
  'text-object': { width: 150, height: 150 },
  monkey: { width: 150, height: 150 },
  octopus: { width: 150, height: 150 },
  gorilla: { width: 150, height: 150 },
  cupcake: { width: 150, height: 150 },
  bear: { width: 150, height: 150 },
  dino: { width: 150, height: 150 },
  frog: { width: 150, height: 150 },
  greenman: { width: 150, height: 150 },
  mustache: { width: 150, height: 150 },
  spacepod: { width: 150, height: 150 },
  zombieBear: { width: 150, height: 150 },
  ghoulopus: { width: 150, height: 150 },
  bats: { width: 150, height: 150 },
  frankenrilla: { width: 150, height: 150 },
  jodyWitch: { width: 150, height: 150 },
  cauldron: { width: 150, height: 150 },
  pumpkin: { width: 150, height: 150 },
  broom: { width: 150, height: 150 },
  lantern: { width: 150, height: 150 },
  parrotFlying: { width: 150, height: 150 },
  mandrill: { width: 150, height: 150 },
  mosquito: { width: 150, height: 150 },
  missChief: { width: 150, height: 150 },
  venus: { width: 150, height: 150 },
  jeepers: { width: 390, height: 390 },
  banyan: { width: 600, height: 600 },
  stargirl: { width: 172.5, height: 172.5 },
  astro: { width: 187.5, height: 187.5 },
  chillanna: { width: 172.5, height: 172.5 },
  robo: { width: 150, height: 150 },
  raccoon: { width: 172.5, height: 172.5 },
  bird: { width: 150, height: 150 },
  square: { width: 150, height: 150 },
  circle: { width: 150, height: 150 },
  hexagon: { width: 150, height: 150 },
  triangle: { width: 150, height: 150 },
  rightTriangle: { width: 150, height: 150 },
  rectangle: { width: 150, height: 150 },
  heart: { width: 150, height: 150 },
  star: { width: 150, height: 150 },
  arch: { width: 150, height: 150 },
  parallelogram: { width: 150, height: 150 },
  squiggle: { width: 150, height: 150 },
  donut: { width: 150, height: 150 },
  tetrisZ: { width: 150, height: 150 },
  tetrisT: { width: 150, height: 150 },
  tetrisL: { width: 150, height: 150 },
  corner: { width: 150, height: 150 },
  flower: { width: 150, height: 150 },
  threeProngedBoomerang: { width: 150, height: 150 },
  squishedBox: { width: 150, height: 150 },
  bead: { width: 150, height: 150 },
  chevron: { width: 150, height: 150 },
  xShape: { width: 150, height: 150 },
  tetrisLine: { width: 150, height: 150 },
  toucan: { width: 150, height: 150 },
  anteater: { width: 150, height: 150 },
  crocodile: { width: 150, height: 150 },
  sloth: { width: 150, height: 150 },
  iguana: { width: 150, height: 150 },
  hut: { width: 150, height: 150 },
  penguin: { width: 150, height: 150 },
  winterQueen: { width: 225, height: 225 },
  shyYeti: { width: 300, height: 300 },
  deer: { width: 255, height: 255 },
  elf: { width: 150, height: 150 },
  snowGlobe: { width: 450, height: 450 },
  polarbear: { width: 300, height: 300 },
  sleigh: { width: 300, height: 300 },
  mistletoe: { width: 129, height: 129 },
  snowMan: { width: 225, height: 225 },
  snowflake: { width: 45, height: 45 },
  tetrisTFullSize: { width: 150, height: 100 },
  rectangleFullSize: { width: 150, height: 112.5 },
  archFullSize: { width: 150, height: 75 },
  rightTriangleFullSize: { width: 150, height: 150 },
  tetrisLFullSize: { width: 150, height: 100 },
  roundedSquareFullSize: { width: 150, height: 150 },
  roundedRightTriangleFullSize: { width: 150, height: 150 },
  squishedBoxFullSize: { width: 150, height: 150 },
  starFullSize: { width: 150, height: 142.66 },
  parallelogramTallFullSize: { width: 150, height: 150 },
  arrowRoundedFullSize: { width: 106.07, height: 150 },
  hexagonFullSize: { width: 150, height: 129.9 },
  squiggleFullSize: { width: 150, height: 69.64 },
  squareFullSize: { width: 150, height: 150 },
  chevronFullSize: { width: 150, height: 129.9 },
  flowerFullSize: { width: 150, height: 150 },
  heartFullSize: { width: 150, height: 126.77 },
  parallelogramWideFullSize: { width: 150, height: 75 },
  beadFullSize: { width: 150, height: 76.95 },
  triangleFullSize: { width: 150, height: 129.9 },
  xFullSize: { width: 150, height: 150 },
  donutFullSize: { width: 150, height: 150 },
  tetrisZFullSize: { width: 150, height: 100 },
  tetrisLineFullSize: { width: 200, height: 50 },
  fanbladeFullSize: { width: 150, height: 137.26 },
  cornerFullSize: { width: 150, height: 150 },
  circleFullSize: { width: 150, height: 150 },
  tetrisTV3: { width: 150, height: 100 },
  rectangleV3: { width: 150, height: 100 },
  archV3: { width: 150, height: 75 },
  tetrisLV3: { width: 150, height: 100 },
  starV3: { width: 150, height: 143 },
  arrowRoundedV3: { width: 106, height: 150 },
  hexagonV3: { width: 150, height: 130 },
  squiggleV3: { width: 150, height: 70 },
  chevronV3: { width: 150, height: 130 },
  heartV3: { width: 150, height: 127 },
  parallelogramWideV3: { width: 150, height: 75 },
  beadV3: { width: 150, height: 77 },
  triangleV3: { width: 150, height: 130 },
  tetrisZV3: { width: 150, height: 100 },
  fanbladeV3: { width: 150, height: 137 },
  crocodileJaws: { width: 288, height: 164 },
  lanternFullSize: { width: 48, height: 84 }
}
const paramTypes = {
  42: 'Default',
  43: 'LineWidth',
  44: 'LineColor',
  45: 'RandomLow',
  46: 'RandomHigh',
  47: 'Variable',
  48: 'VariableValue',
  49: 'Conditional',
  50: 'HSObject',
  51: 'Sound',
  52: 'Event',
  53: 'SetText',
  54: 'Object',
  55: 'TextOnly',
  56: 'Scene',
  57: 'MultiPurposeNumberDefault',
  58: 'Product',
  59: 'Rhythm',
  60: 'MusicNote',
  61: 'Instrument',
  62: 'ProjectUUID',
  63: 'TrailCap'
}
const rhythmNames = {
  '1.0': 'Whole',
  0.5: 'Half',
  0.25: 'Quarter',
  0.125: 'Eighth',
  0.0625: 'Sixteenth',
  0.03125: '32nd',
  0.16666667: '4th Triplet',
  0.083333336: '8th Triplet',
  0.041666668: '16th Triplet',
  0.020833334: '32nd Triplet',
  1.5: 'Dotted Whole',
  0.75: 'Dotted Half',
  0.375: 'Dotted 4th',
  0.1875: 'Dotted 8th',
  0.09375: 'Dotted 16th'
}

const globals = {
  ProjectUpdater,
  ProjectVariable,
  ProjectTrait,
  ProjectEventParameter,
  ProjectAbility,
  ProjectRule,
  ProjectCustomRule,
  ProjectCustomRuleInstance,
  ProjectStageObject,
  ProjectScene,
  ProjectCustomImage,
  EditorAnalytics,
  blockLabels,
  charLabels,
  characterSizes,
  paramTypes,
  rhythmNames
}
Object.assign(globalThis, globals)
export {
  ProjectUpdater,
  ProjectVariable,
  ProjectTrait,
  ProjectEventParameter,
  ProjectAbility,
  ProjectRule,
  ProjectCustomRule,
  ProjectCustomRuleInstance,
  ProjectStageObject,
  ProjectScene,
  ProjectCustomImage,
  EditorAnalytics,
  blockLabels,
  charLabels,
  characterSizes,
  paramTypes,
  rhythmNames
}
